/*
Waypoints Infinite Scroll Shortcut - 4.0.0
http://imakewebthings.com/waypoints/shortcuts/infinite-scroll
Copyright © 2011-2015 Caleb Troughton
Licensed under the MIT license.
https://github.com/imakewebthings/waypoints/blob/master/licenses.txt
*/

import 'waypoints/lib/noframework.waypoints';

declare global {
  interface Window {
    Waypoint: new (options: WaypointOptions) => Waypoint;
  }
}

interface BaseInfiniteOptions {
  container: 'auto' | HTMLElement;
  items: string;
  loadingClass: string;
  more: string;
  offset: string;
  onBeforePageLoad: () => void;
  onAfterPageLoad: (items: HTMLElement[]) => void;
}

interface InfiniteOptions extends Partial<BaseInfiniteOptions> {
  element: HTMLElement;
}

type InfiniteWaypointOptions = BaseInfiniteOptions & WaypointOptions;

type WaypointHandler = (this: Waypoint, direction?: string) => void;

function parseHTML(html: string): HTMLElement {
  const element = document.implementation.createHTMLDocument();
  element.body.innerHTML = html;
  return element.body;
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export default class Infinite {
  private defaults: BaseInfiniteOptions = {
    container: 'auto',
    items: '.infinite-item',
    more: '.infinite-more-link',
    offset: 'bottom-in-view',
    loadingClass: 'infinite-loading',
    onBeforePageLoad() {
      return;
    },
    onAfterPageLoad() {
      return;
    },
  };

  private options: BaseInfiniteOptions & PartialBy<WaypointOptions, 'handler'>;
  private container?: HTMLElement;
  private more?: HTMLElement | null;
  private waypoint?: Waypoint;
  private handler?: WaypointHandler;

  constructor(options: InfiniteOptions) {
    this.options = Object.assign({}, this.defaults, options);
    this.container = this.options.container === 'auto' ? this.options.element : this.options.container;
    this.more = document.querySelector<HTMLElement>(this.options.more);

    if (!this.container) return;
    if (this.options.container !== 'auto') this.container = this.options.container;

    if (this.more) {
      this.options.handler = this.handler = this.setupHandler();
      this.waypoint = new window.Waypoint(this.options as InfiniteWaypointOptions);
    }
  }

  triggerHandler(): void {
    if (this.handler && this.waypoint) this.handler.apply(this.waypoint);
  }

  /* Private */
  private setupHandler(): WaypointHandler {
    return this.handleWaypoint;
  }

  private handleWaypoint = (): void => {
    this.options.onBeforePageLoad();
    this.destroy();

    const more = this.more;
    const url = more?.getAttribute('href');

    if (more && url) {
      this.more = undefined;

      this.container?.classList.add(this.options.loadingClass);

      fetch(url)
        .then((data) => data.text())
        .then((html) => {
          const data = parseHTML(html);
          const newMore = data.querySelector<HTMLElement>(this.options.more);
          const items = [...data.querySelectorAll<HTMLElement>(this.options.items)];

          if (items.length > 0) this.container?.append(...items);

          this.container?.classList.remove(this.options.loadingClass);

          if (newMore) {
            more.replaceWith(newMore);
            this.more = newMore;

            // Delay adding the new waypoint for half a second to let the gallery update properly.
            setTimeout(() => {
              this.waypoint = new window.Waypoint(this.options as InfiniteWaypointOptions);
            }, 250);
          } else {
            more.remove();
          }

          this.options.onAfterPageLoad(items);
        })
        .catch(() => {
          alert('Unable to load more photos');
        });
    }
  };

  canLoadMore(): boolean {
    return !!this.more;
  }

  destroy(): void {
    if (this.waypoint) {
      this.waypoint.destroy();
    }
  }
}
